import { useSectionPermission, type PermissionSection } from '@artic/api-client';
import { FullHeightChrome } from '@artic/ui/full-height-layout';
import { useRouterState } from '@tanstack/react-router';

import { ReadOnlyBanner } from './read-only';
import { sectionRequirementFor } from './route-guard';

type RouteMatches = Parameters<typeof sectionRequirementFor>[0];

/**
 * The section whose read-only state the central banner reflects for these
 * route matches, or `null` when the page gets no central banner:
 * - no section (the app shell, staff tools);
 * - Factory Access routes (job cards, work orders) — an all-or-nothing
 *   factory grant with no read/write split;
 * - Materials, whose pages carry their own read-only treatment;
 * - write-only routes (`permissionLevel: 'write'`), which a reader can't
 *   reach in the first place.
 */
export function readOnlyBannerSection(matches: RouteMatches): PermissionSection | null {
  const requirement = sectionRequirementFor(matches);
  if (!requirement || requirement.section === 'factory_access') return null;
  if (requirement.section === 'materials') return null;
  if (requirement.level === 'write') return null;
  return requirement.section;
}

/**
 * Permissions v1: the one read-only banner per page. Mounted by the root
 * layout above the route outlet (`fullHeight` for the full-height layout), so pages never render their own banner for
 * the partner-wide "read everywhere" case. Factory-scoped pages add
 * `FactoryReadOnlyBanner` for the narrower "read in this factory" case.
 */
export function RouteReadOnlyBanner({ fullHeight = false }: { fullHeight?: boolean }) {
  const section = useRouterState({ select: (state) => readOnlyBannerSection(state.matches) });
  if (!section) return null;
  return <SectionReadOnlyBanner section={section} fullHeight={fullHeight} />;
}

function SectionReadOnlyBanner({
  section,
  fullHeight,
}: {
  section: PermissionSection;
  fullHeight: boolean;
}) {
  const { canWrite } = useSectionPermission(section);
  if (canWrite) return null;
  // A full-height page owns its own padding, so the banner brings chrome
  // padding matching the pages' toolbars; the default layout already pads.
  if (fullHeight) {
    return (
      <FullHeightChrome className="px-4 py-2">
        <ReadOnlyBanner />
      </FullHeightChrome>
    );
  }
  return <ReadOnlyBanner />;
}
